/**
 * Save storage — v0M15.2.
 *
 * Thin layer over `window.localStorage` that keeps the sim's `SavedGame`
 * blobs in named slots. The slot picker (`menu/save-slot-picker.ts`)
 * consumes this layer; higher-level flows (in-game Save menu, Main Menu
 * Load, post-victory Load-Previous) go through the picker.
 *
 * Storage key convention:
 *   `su:save:<slotId>`       — the full serialised SavedGame JSON string
 *   `su:save:<slotId>:meta`  — a small JSON metadata blob (~200 bytes)
 *
 * The metadata sits alongside the full save so `listSlots()` can render
 * the picker (slot name / timestamp / game day / scenario) without
 * parsing every save blob. Callers write both atomically in `writeSlot`
 * and delete both atomically in `deleteSlot`.
 *
 * Slots:
 *   - `slot1`..`slot5` — user-named save slots
 *   - `autosave`       — reserved for the autosave system (v0M15.5).
 *                        Not writable via the picker's Save button.
 *
 * Design lock (v0M15-design): localStorage-only for v1. File export /
 * import is a post-v1 candidate. Cap per origin is ~5 MB in every
 * modern browser; a Karth-scale campaign save is ~1-2 MB with all
 * factions' visibility maps, so 6 slots fit comfortably.
 */

import type { SavedGame } from '../sim/save';
import {
  serialiseSavedGame,
  deserialiseSavedGame,
  SAVE_SCHEMA_VERSION,
} from '../sim/save';

/** Slot identifier — narrow union so mistyped slots fail at compile time. */
export type SlotId = 'slot1' | 'slot2' | 'slot3' | 'slot4' | 'slot5' | 'autosave';

/** Ordered list, safe to iterate for rendering the picker. */
export const ALL_SLOT_IDS: readonly SlotId[] = [
  'slot1',
  'slot2',
  'slot3',
  'slot4',
  'slot5',
  'autosave',
];

/**
 * Small metadata blob stored beside each save. Keep this shape stable —
 * callers list slots without parsing the full save, so this is the
 * source of truth for picker rendering.
 */
export interface SlotMeta {
  /** User-supplied name, or an auto-generated default (e.g. 'Autosave'). */
  readonly name: string;
  /** Unix timestamp (ms) of the save write. */
  readonly savedAt: number;
  /** Game day at save time — derived from `state.tick / TICKS_PER_GAME_DAY`. */
  readonly gameDay: number;
  /** Scenario identifier for the picker's second-line detail (e.g. 'Karth (Standard)'). */
  readonly scenarioName: string;
  /** Save-schema version this blob was written against. Guards against version drift. */
  readonly schemaVersion: number;
}

/** Row shape returned by `listSlots` — always includes an entry per slot, with null meta for empties. */
export interface SlotEntry {
  readonly id: SlotId;
  readonly meta: SlotMeta | null;
}

function keyFor(id: SlotId): string {
  return `su:save:${id}`;
}
function metaKeyFor(id: SlotId): string {
  return `su:save:${id}:meta`;
}

/**
 * Return every slot's current state. Populated slots include their
 * metadata; empty slots have `meta: null`. Order matches `ALL_SLOT_IDS`.
 * Never throws on missing / corrupt metadata — treats those as empty.
 */
export function listSlots(): readonly SlotEntry[] {
  return ALL_SLOT_IDS.map((id): SlotEntry => {
    const metaRaw = safeGetItem(metaKeyFor(id));
    const payloadPresent = safeGetItem(keyFor(id)) !== null;
    if (!payloadPresent) {
      return { id, meta: null };
    }
    if (metaRaw === null) {
      // Payload exists but metadata is missing — treat as empty for
      // picker purposes rather than exposing a half-written slot.
      return { id, meta: null };
    }
    try {
      const meta = JSON.parse(metaRaw) as SlotMeta;
      if (
        typeof meta.name === 'string' &&
        typeof meta.savedAt === 'number' &&
        typeof meta.gameDay === 'number' &&
        typeof meta.scenarioName === 'string' &&
        typeof meta.schemaVersion === 'number'
      ) {
        return { id, meta };
      }
      return { id, meta: null };
    } catch {
      return { id, meta: null };
    }
  });
}

/**
 * Read a slot's SavedGame. Returns null if the slot is empty or the
 * stored blob fails to parse (schema mismatch, corruption). Callers
 * should treat a null return as "slot is unloadable" and surface a
 * user-facing message where appropriate.
 */
export function readSlot(id: SlotId): SavedGame | null {
  const raw = safeGetItem(keyFor(id));
  if (raw === null) return null;
  try {
    return deserialiseSavedGame(raw);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[SU] save-storage.readSlot(${id}): ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
}

/**
 * Write a SavedGame to a slot, with a display name and derived metadata.
 * Both the payload and metadata keys are written; on quota-exceeded the
 * caller receives a thrown error that includes the storage-quota hint.
 *
 * Compute-heavy fields (`gameDay`, `scenarioName`) are pulled from the
 * SavedGame here rather than requiring the caller to duplicate them.
 */
export function writeSlot(id: SlotId, saved: SavedGame, name: string): void {
  const payload = serialiseSavedGame(saved);
  const meta: SlotMeta = {
    name,
    savedAt: Date.now(),
    gameDay: gameDayFromTick(saved.state.tick),
    scenarioName: scenarioDisplayName(saved.scenario.mapName, saved.scenario.startingMode),
    schemaVersion: SAVE_SCHEMA_VERSION,
  };
  try {
    // Write metadata FIRST so if the payload write fails on quota, the
    // meta lookup won't advertise a slot that has no readable content.
    // (Meta is tiny — ~200 bytes — so it always fits.)
    safeSetItem(metaKeyFor(id), JSON.stringify(meta));
    safeSetItem(keyFor(id), payload);
  } catch (err) {
    // If the payload write failed, clean up the meta write so listSlots
    // doesn't show a phantom slot.
    safeRemoveItem(metaKeyFor(id));
    throw new Error(
      `save-storage.writeSlot(${id}): ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

/** Remove both payload + metadata for a slot. No-op if slot is empty. */
export function deleteSlot(id: SlotId): void {
  safeRemoveItem(keyFor(id));
  safeRemoveItem(metaKeyFor(id));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** MECHANICS.md §1.1 — MFix3.8 raised this from 5 to 25 ticks/day. */
const TICKS_PER_GAME_DAY = 25;

function gameDayFromTick(tick: number): number {
  return Math.floor(tick / TICKS_PER_GAME_DAY);
}

/**
 * Format a scenario for the picker's second-line label. Kept in this
 * file so the display convention is co-located with the metadata that
 * consumes it.
 */
function scenarioDisplayName(mapName: string, startingMode: string): string {
  const mapLabel = mapName === 'karth' ? 'Karth' : mapName;
  const modeLabel =
    startingMode === 'single-province'
      ? 'Single Province'
      : startingMode === 'country-divided'
      ? 'Country Divided'
      : startingMode === 'country-united'
      ? 'Country United'
      : startingMode;
  return `${mapLabel} — ${modeLabel}`;
}

/** localStorage.getItem guarded against missing global (SSR / stripped test env). */
function safeGetItem(key: string): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** localStorage.setItem — throws on quota or if the global is absent. */
function safeSetItem(key: string, value: string): void {
  if (typeof localStorage === 'undefined') {
    throw new Error('localStorage is not available in this environment');
  }
  localStorage.setItem(key, value);
}

function safeRemoveItem(key: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch {
    // Best-effort — a failed remove is not user-visible.
  }
}
